import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Megaphone, Send, Type } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Label } from "./ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminBroadcastEditor = () => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const insertTag = (startTag, endTag) => {
    const textarea = document.getElementById('broadcast-editor');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;

    const newText = text.substring(0, start) + startTag + text.substring(start, end) + endTag + text.substring(end);
    setMessage(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + startTag.length, end + startTag.length);
    }, 0);
  };

  const insertColor = (colorClass) => {
    insertTag(`<span class="${colorClass}">`, '</span>');
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Message cannot be empty");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/broadcast`, {
        message: message
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Broadcast sent successfully!");
      setMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-card/50 border-amber-500/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-500">
          <Megaphone className="w-5 h-5" />
          Server Broadcast
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => insertTag('<b>', '</b>')}>
              <Type className="w-4 h-4 mr-1 font-bold" /> Bold
            </Button>
            <Button size="sm" variant="outline" onClick={() => insertTag('<i>', '</i>')}>
              <Type className="w-4 h-4 mr-1 italic" /> Italic
            </Button>
            <Button size="sm" variant="outline" onClick={() => insertTag('<span style="text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;">', '</span>')}>
              Outline
            </Button>

            <div className="flex items-center gap-1 border rounded-md p-1 bg-background">
              <span className="text-xs text-muted-foreground mr-1 px-1">Colors:</span>
              <button onClick={() => insertColor('text-red-500')} className="w-6 h-6 rounded-full bg-red-500 border border-border hover:scale-110 transition-transform"></button>
              <button onClick={() => insertColor('text-blue-500')} className="w-6 h-6 rounded-full bg-blue-500 border border-border hover:scale-110 transition-transform"></button>
              <button onClick={() => insertColor('text-green-500')} className="w-6 h-6 rounded-full bg-green-500 border border-border hover:scale-110 transition-transform"></button>
              <button onClick={() => insertColor('text-yellow-500')} className="w-6 h-6 rounded-full bg-yellow-500 border border-border hover:scale-110 transition-transform"></button>
              <button onClick={() => insertColor('text-purple-500')} className="w-6 h-6 rounded-full bg-purple-500 border border-border hover:scale-110 transition-transform"></button>
              <button onClick={() => insertColor('text-pink-500')} className="w-6 h-6 rounded-full bg-pink-500 border border-border hover:scale-110 transition-transform"></button>
              <button onClick={() => insertColor('text-white')} className="w-6 h-6 rounded-full bg-white border border-border hover:scale-110 transition-transform"></button>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="broadcast-editor">Message</Label>
            <textarea
              id="broadcast-editor"
              className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
              placeholder="Type your broadcast message here. Use buttons to add styling tags or write raw HTML."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Select text and click formatting buttons to style, or manually type HTML.</p>
          </div>

          {message && (
            <div className="border rounded-md p-4 bg-background/50">
              <Label className="mb-2 block text-xs text-muted-foreground">Live Preview (similar to notification):</Label>
              <div
                className="text-lg whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={{ __html: message }}
              />
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSend}
              disabled={loading || !message.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              {loading ? "Sending..." : "Send Broadcast"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminBroadcastEditor;
